import React from "react";
import { Link } from "react-router-dom";
import { MdTrendingUp } from "react-icons/md";
import Card from "../../../../components/card";
import { WeeklyUtilizationPoint } from "../../../../interfaces/Dashboard";

/**
 * Utilização das próximas semanas — o único bloco da home que olha para frente.
 *
 * É onde um buraco de venda aparece **antes** de virar mês fechado no vermelho: semana verde-claro
 * lá na frente é capacidade ociosa que ainda dá tempo de preencher.
 *
 * Barras em CSS: são ~6 pontos com um eixo. Carregar ApexCharts (≈500 KB, sem tree-shaking) para
 * isso não se paga — a biblioteca fica reservada para a série de 12 meses.
 */

const BAR_COLOR: Record<string, string> = {
  green: "bg-gradient-to-t from-emerald-500 to-emerald-400",
  yellow: "bg-gradient-to-t from-amber-500 to-amber-400",
  red: "bg-gradient-to-t from-red-500 to-rose-400",
};

const UtilizationForecastCard: React.FC<{ points: WeeklyUtilizationPoint[] }> = ({ points }) => {
  // Escala pelo maior entre utilização e 100%, para a linha de capacidade nunca sair do quadro.
  const max = Math.max(100, ...points.map((p) => p.utilizationPct));

  return (
    <Card extra="p-5">
      <div className="mb-1 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <MdTrendingUp className="h-5 w-5 text-dv-blue-500 dark:text-dv-cyan-300" aria-hidden="true" />
          <h3 className="text-base font-bold text-navy-700 dark:text-white">Próximas semanas</h3>
        </div>
        <Link
          to="/admin/capacity"
          className="text-xs font-semibold text-dv-blue-600 hover:underline dark:text-dv-cyan-300"
        >
          Ver capacidade
        </Link>
      </div>
      <p className="mb-5 text-xs text-gray-700 dark:text-gray-300">
        Horas planejadas sobre a capacidade do time. Barra baixa lá na frente é capacidade ainda
        vendável.
      </p>

      {points.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-gray-200 py-8 text-center dark:border-gray-700">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Sem dados de capacidade.</p>
        </div>
      ) : (
        <>
          <div className="relative flex h-40 items-end justify-between gap-2">
            {/* Referência dos 100% de capacidade */}
            <div
              className="pointer-events-none absolute inset-x-0 border-t border-dashed border-gray-300 dark:border-gray-600"
              style={{ bottom: `${(100 / max) * 100}%` }}
              aria-hidden="true"
            >
              <span className="absolute -top-4 right-0 text-[10px] font-semibold text-gray-500 dark:text-gray-400">
                100%
              </span>
            </div>

            {points.map((p) => (
              <div key={p.weekStart} className="flex h-full flex-1 flex-col items-center justify-end gap-1">
                <span className="text-[11px] font-semibold tabular-nums text-navy-700 dark:text-white">
                  {Math.round(p.utilizationPct)}%
                </span>
                <div
                  className={`w-full rounded-t-lg transition-all duration-500 ${
                    BAR_COLOR[p.utilizationColor] ?? BAR_COLOR.green
                  }`}
                  style={{ height: `${Math.max(2, (p.utilizationPct / max) * 100)}%` }}
                  title={`${p.plannedHours} h planejadas de ${p.capacityHours} h`}
                />
              </div>
            ))}
          </div>

          <div className="mt-2 flex justify-between gap-2">
            {points.map((p) => (
              <span
                key={p.weekStart}
                className="flex-1 text-center text-[11px] tabular-nums text-gray-500 dark:text-gray-400"
              >
                {p.label}
              </span>
            ))}
          </div>
        </>
      )}
    </Card>
  );
};

export default UtilizationForecastCard;
